import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { InputSwitch } from 'primereact/inputswitch';
import { Input } from 'src/components/ui/input';
import { cn } from 'src/lib/utils';
import { snapToThirtyMinuteSlot } from 'src/helpers/scheduler-time.helpers';
import { validateCronExpression } from 'src/helpers/scheduler-schedule.helpers';
import type {
  RegisteredSchedulerTask,
  ScheduleType,
  ScheduledTask,
} from '../types/scheduler.types';
import { SchedulerTimePicker } from './scheduler-time-picker';
import {
  SchedulerDsButton,
  schedulerDsSegmentButtonClass,
  schedulerDsSegmentGroup,
} from './scheduler-ds-button';

export interface SchedulerTaskFormValues {
  name: string;
  task_path: string;
  schedule_type: ScheduleType;
  scheduled_time?: string | null;
  cron_expression?: string | null;
  is_enabled: boolean;
}

interface SchedulerTaskFormProps {
  registry: RegisteredSchedulerTask[];
  initialTask?: ScheduledTask | null;
  isSaving?: boolean;
  submitError?: string | null;
  onSubmit: (values: SchedulerTaskFormValues) => void;
  onCancel?: () => void;
}

const selectClassName = cn(
  'h-10 w-full appearance-none rounded border border-gray-400 bg-white pl-4 pr-10 text-base text-black',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

const labelClassName = 'text-sm font-medium text-gray-700';

type ScheduleModeButtonProps = {
  active: boolean;
  onClick: () => void;
  children: string;
};

function ScheduleModeButton({ active, onClick, children }: ScheduleModeButtonProps) {
  return (
    <button
      type="button"
      className={schedulerDsSegmentButtonClass(active)}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function SchedulerTaskForm({
  registry,
  initialTask,
  isSaving = false,
  submitError = null,
  onSubmit,
  onCancel,
}: SchedulerTaskFormProps) {
  const [name, setName] = useState(initialTask?.name ?? '');
  const [taskPath, setTaskPath] = useState(initialTask?.task_path ?? registry[0]?.path ?? '');
  const [scheduleType, setScheduleType] = useState<ScheduleType>(
    initialTask?.schedule_type ?? 'time'
  );
  const [scheduledTime, setScheduledTime] = useState(
    snapToThirtyMinuteSlot(initialTask?.scheduled_time ?? undefined)
  );
  const [cronExpression, setCronExpression] = useState(initialTask?.cron_expression ?? '0 9 * * *');
  const [isEnabled, setIsEnabled] = useState(initialTask?.is_enabled ?? true);

  useEffect(() => {
    if (initialTask) {
      setName(initialTask.name);
      setTaskPath(initialTask.task_path);
      setScheduleType(initialTask.schedule_type);
      setScheduledTime(snapToThirtyMinuteSlot(initialTask.scheduled_time ?? undefined));
      setCronExpression(initialTask.cron_expression ?? '0 9 * * *');
      setIsEnabled(initialTask.is_enabled);
    }
  }, [initialTask]);

  useEffect(() => {
    if (!initialTask && registry.length && !taskPath) {
      setTaskPath(registry[0].path);
    }
  }, [initialTask, registry, taskPath]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (scheduleType === 'cron' && validateCronExpression(cronExpression)) {
      return;
    }
    onSubmit({
      name: name.trim(),
      task_path: taskPath,
      schedule_type: scheduleType,
      scheduled_time: scheduleType === 'time' ? `${scheduledTime}:00` : null,
      cron_expression: scheduleType === 'cron' ? cronExpression.trim() : null,
      is_enabled: isEnabled,
    });
  };

  const cronError = scheduleType === 'cron' ? validateCronExpression(cronExpression) : null;

  const canSubmit =
    name.trim().length > 0
    && taskPath.length > 0
    && registry.length > 0
    && (scheduleType === 'time' || !cronError);

  return (
    <form
      className="space-y-5"
      onSubmit={handleSubmit}
    >
      <div className="space-y-2">
        <label
          htmlFor="scheduler-task-name"
          className={labelClassName}
        >
          Task name
        </label>
        <Input
          id="scheduler-task-name"
          className="h-10 rounded border-gray-400 px-4 text-base placeholder:text-gray-500"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <label
          htmlFor="scheduler-task-path"
          className={labelClassName}
        >
          Registered task
        </label>
        {registry.length ? (
          <div className="relative">
            <select
              id="scheduler-task-path"
              className={selectClassName}
              value={taskPath}
              onChange={(event) => setTaskPath(event.target.value)}
              disabled={isSaving}
              required
            >
              {registry.map((item) => (
                <option
                  key={item.path}
                  value={item.path}
                >
                  {item.label}
                  {item.weekdays_only ? ' (weekdays)' : ''}
                </option>
              ))}
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-gray-500"
              aria-hidden
            />
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No registered tasks are available to schedule.
          </p>
        )}
        <p className="text-xs text-gray-500">
          Registered tasks are job types you can schedule — they are not runs until you save one below.
        </p>
      </div>

      <div className="space-y-3">
        <div className="space-y-2">
          <span className={labelClassName}>
            Schedule type
          </span>
          <div className={schedulerDsSegmentGroup}>
            <ScheduleModeButton
              active={scheduleType === 'time'}
              onClick={() => setScheduleType('time')}
            >
              Time (ET)
            </ScheduleModeButton>
            <ScheduleModeButton
              active={scheduleType === 'cron'}
              onClick={() => setScheduleType('cron')}
            >
              Cron
            </ScheduleModeButton>
          </div>
        </div>

        {scheduleType === 'time' ? (
          <div className="space-y-2">
            <label
              htmlFor="scheduler-task-time"
              className={labelClassName}
            >
              Schedule time (Eastern)
            </label>
            <SchedulerTimePicker
              id="scheduler-task-time"
              value={scheduledTime}
              onChange={setScheduledTime}
              disabled={isSaving}
            />
            <p className="text-xs text-gray-500">
              Runs on 30-minute intervals when the Cloud Scheduler tick fires.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <label
              htmlFor="scheduler-task-cron"
              className={labelClassName}
            >
              Cron expression
            </label>
            <Input
              id="scheduler-task-cron"
              className={cn(
                'h-10 rounded border-gray-400 px-4 font-mono text-sm placeholder:text-gray-500',
                cronError && 'border-red-502 focus-visible:ring-red-502'
              )}
              value={cronExpression}
              onChange={(event) => setCronExpression(event.target.value)}
              placeholder="0 9 * * *"
              required
              disabled={isSaving}
              aria-invalid={cronError ? true : undefined}
              aria-describedby={cronError ? 'scheduler-task-cron-error' : undefined}
            />
            {cronError ? (
              <p
                id="scheduler-task-cron-error"
                className="text-xs text-red-700"
                role="alert"
              >
                {cronError}
              </p>
            ) : null}
            <p className="text-xs text-gray-500">
              Standard 5- or 6-field cron in Eastern time, e.g.
              {' '}
              <code className="font-mono">
                0 9 * * *
              </code>
              {' '}
              (daily at 9:00 ET).
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 rounded-md border border-gray-400 bg-gray-200 px-4 py-3">
        <InputSwitch
          inputId="scheduler-task-enabled"
          checked={isEnabled}
          onChange={(event) => setIsEnabled(!!event.value)}
        />
        <label
          htmlFor="scheduler-task-enabled"
          className="text-sm text-gray-700"
        >
          Task enabled
        </label>
      </div>

      {submitError ? (
        <p
          className="text-sm text-red-700"
          role="alert"
        >
          {submitError}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-3 pt-2">
        <SchedulerDsButton
          type="submit"
          variant="accent"
          disabled={isSaving || !canSubmit}
        >
          {initialTask ? 'Save changes' : 'Create task'}
        </SchedulerDsButton>
        {onCancel ? (
          <SchedulerDsButton
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </SchedulerDsButton>
        ) : null}
      </div>
    </form>
  );
}
